import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { InboxEntry } from '@this+that/models';
import log from 'loglevel';

import { useActiveInboxEntriesSlice } from './useActiveInboxEntriesSlice';
import { useDismissResolutionSuggestionService } from '../../services/inboxEntries.service';
import { useCustomToast } from '../useCustomToast';

// The server answers with the row as it is when it had nothing left to
// dismiss: a flag already cleared by a resolve, or a newer one than the user saw.
const wasDismissed = (entry: InboxEntry): boolean =>
  !entry.resolvedAt && !entry.resolutionSuggestedAt;

// "Not done yet" on a DoBox the system flagged as looking resolved. The server
// remembers the evidence, so the same message never raises the flag again.
// Never rejects: the callers await it straight from a click handler.
export const useDismissResolutionSuggestion = () => {
  const { t } = useTranslation();
  const { dismissResolutionSuggestion: dismissOnServer } =
    useDismissResolutionSuggestionService();
  const { updateInboxEntry } = useActiveInboxEntriesSlice();
  const { successMessage, warningMessage, errorMessage } = useCustomToast();
  const [isDismissing, setIsDismissing] = useState(false);

  const dismissResolutionSuggestion = useCallback(
    async (entry: InboxEntry): Promise<boolean> => {
      setIsDismissing(true);
      try {
        const current = await dismissOnServer(
          entry.id,
          entry.resolutionSuggestedAt,
        );
        if (!current) {
          throw new Error('Failed to dismiss the resolution suggestion');
        }
        updateInboxEntry(current);

        if (!wasDismissed(current)) {
          warningMessage(t('doBox.looksResolved.alreadyChanged'));
          return false;
        }
        successMessage(t('doBox.looksResolved.dismissSuccess'));
        return true;
      } catch (error) {
        log.error('Error dismissing resolution suggestion:', error);
        errorMessage(t('doBox.looksResolved.dismissError'));
        return false;
      } finally {
        setIsDismissing(false);
      }
    },
    [
      dismissOnServer,
      updateInboxEntry,
      successMessage,
      warningMessage,
      errorMessage,
      t,
    ],
  );

  return { dismissResolutionSuggestion, isDismissing };
};
